import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, CheckCircle, Circle, ChevronRight, Building2, User, Mail,
  Calendar, IndianRupee, FileText, Image, Clock, AlertTriangle, Lock,
  Edit2, Loader2, RefreshCw, XCircle, RotateCcw, Home, FileSpreadsheet, Upload,
  MessageSquare, Send, History
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileMenuTrigger } from '@/components/Layout';
import BreadcrumbNav from '@/components/BreadcrumbNav';

// Format Lacs
const formatLacs = (value) => {
  if (!value && value !== 0) return '—';
  return `₹${parseFloat(value).toFixed(2)} L`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Milestone labels
const MILESTONE_CONFIG = {
  booking_approved: { label: 'Booking Approved', shortLabel: 'Booking', icon: CheckCircle, color: 'emerald' },
  agreement_done: { label: 'Agreement Done', shortLabel: 'Agreement', icon: FileText, color: 'blue' },
  registration_done: { label: 'Registration Done', shortLabel: 'Registration', icon: FileText, color: 'purple' },
  invoice_raised: { label: 'Invoice Raised', shortLabel: 'Invoice', icon: IndianRupee, color: 'amber' },
  revenue_received: { label: 'Revenue Received', shortLabel: 'Revenue', icon: IndianRupee, color: 'emerald' }
};

const MILESTONE_ORDER = ['booking_approved', 'agreement_done', 'registration_done', 'invoice_raised', 'revenue_received'];

const BookingDetail = ({ user }) => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [milestones, setMilestones] = useState({});
  const [currentStage, setCurrentStage] = useState(0);
  const [permissions, setPermissions] = useState({});
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, milestone: null });
  const [editDate, setEditDate] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Cancel dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  // Documents state (Version Controlled)
  const [documents, setDocuments] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('cost_sheet');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadRemarks, setUploadRemarks] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState({ open: false, docType: null, versions: [] });
  
  // Resubmit dialog state (for CA/Rep when query is raised)
  const [showResubmitDialog, setShowResubmitDialog] = useState(false);
  const [resubmitData, setResubmitData] = useState({
    agreement_value_lacs: '',
    unit_number: '',
    tower: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    correction_notes: ''
  });
  const [resubmitting, setResubmitting] = useState(false);
  
  // Query history dialog
  const [showQueryHistory, setShowQueryHistory] = useState(false);

  const CANCEL_REASONS = [
    'Customer requested cancellation',
    'Financial issues',
    'Property issue',
    'Documentation problem',
    'Duplicate booking',
    'Pricing mismatch',
    'Other'
  ];

  const fetchBookingDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getBookingDetail(bookingId);
      const data = response.data;
      setBooking(data.booking);
      setMilestones(data.milestones);
      setCurrentStage(data.current_stage);
      setPermissions({
        isAdmin: data.is_admin,
        canEditRepMilestones: data.can_edit_rep_milestones,
        canEditAdminMilestones: data.can_edit_admin_milestones
      });
    } catch (error) {
      toast.error('Failed to load booking details');
      navigate('/my-bookings');
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => {
    fetchBookingDetail();
  }, [fetchBookingDetail]);

  // Fetch documents for this booking
  const fetchDocuments = useCallback(async () => {
    try {
      const response = await api.getBookingDocuments(bookingId);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchDocuments();
    }
  }, [bookingId, fetchDocuments]);

  // Upload document handler
  const handleUploadDocument = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    try {
      // First upload the file
      const formData = new FormData();
      formData.append('file', uploadFile);
      const uploadResponse = await api.uploadBookingDocument(formData, uploadDocType);
      const fileUrl = uploadResponse.data.url;

      // Then create the versioned document entry
      await api.uploadBookingDocumentV2(bookingId, {
        doc_type: uploadDocType,
        file_url: fileUrl,
        file_name: uploadFile.name,
        remarks: uploadRemarks || null
      });

      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadRemarks('');
      fetchDocuments();
      fetchBookingDetail(); // Refresh to get updated cost_sheet_url if applicable
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // View version history
  const handleViewVersionHistory = async (docType) => {
    try {
      const response = await api.getDocumentVersionHistory(bookingId, docType);
      setShowVersionHistory({
        open: true,
        docType: docType,
        label: response.data.label,
        versions: response.data.versions || []
      });
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  // Open resubmit dialog with current booking data pre-filled
  const openResubmitDialog = () => {
    if (!booking) return;
    setResubmitData({
      agreement_value_lacs: booking.agreement_value_lacs || '',
      unit_number: booking.unit_no || booking.unit_number || '',
      tower: booking.tower || '',
      customer_name: booking.customer_name || '',
      customer_phone: booking.customer_phone || '',
      customer_email: booking.customer_email || '',
      correction_notes: ''
    });
    setShowResubmitDialog(true);
  };

  // Handle CA/Rep resubmit booking
  const handleResubmitBooking = async () => {
    if (!resubmitData.correction_notes?.trim()) {
      toast.error('Please provide correction notes explaining what was fixed');
      return;
    }
    
    setResubmitting(true);
    try {
      const submitData = {
        correction_notes: resubmitData.correction_notes
      };
      
      // Only include changed fields
      if (resubmitData.agreement_value_lacs && parseFloat(resubmitData.agreement_value_lacs) !== booking.agreement_value_lacs) {
        submitData.agreement_value_lacs = parseFloat(resubmitData.agreement_value_lacs);
      }
      if (resubmitData.unit_number && resubmitData.unit_number !== (booking.unit_no || booking.unit_number)) {
        submitData.unit_number = resubmitData.unit_number;
      }
      if (resubmitData.tower && resubmitData.tower !== booking.tower) {
        submitData.tower = resubmitData.tower;
      }
      if (resubmitData.customer_name && resubmitData.customer_name !== booking.customer_name) {
        submitData.customer_name = resubmitData.customer_name;
      }
      if (resubmitData.customer_phone && resubmitData.customer_phone !== booking.customer_phone) {
        submitData.customer_phone = resubmitData.customer_phone;
      }
      if (resubmitData.customer_email && resubmitData.customer_email !== booking.customer_email) {
        submitData.customer_email = resubmitData.customer_email;
      }
      
      await api.caResubmitBooking(bookingId, submitData);
      toast.success('Booking resubmitted successfully! Sent for City Head review.');
      setShowResubmitDialog(false);
      setResubmitData({ agreement_value_lacs: '', unit_number: '', tower: '', customer_name: '', customer_phone: '', customer_email: '', correction_notes: '' });
      fetchBookingDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit booking');
    } finally {
      setResubmitting(false);
    }
  };

  // Check if current user can resubmit (status is QUERY_RAISED and user is rep or admin)
  const canResubmit = () => {
    if (!booking || !user) return false;
    const status = booking.status || booking.verification_status;
    const isQueryRaised = status === 'QUERY_RAISED';
    // Allow both reps (who own the booking) and admins to resubmit
    const isRepOrAdmin = permissions.isAdmin || booking.rep_user_id === user.user_id;
    return isQueryRaised && isRepOrAdmin;
  };

  const canEditMilestone = (milestone) => {
    if (milestone === 'booking_approved') return false; // Never editable
    const isRepMilestone = ['agreement_done', 'registration_done'].includes(milestone);
    const isAdminMilestone = ['invoice_raised', 'revenue_received'].includes(milestone);
    
    if (isRepMilestone) return permissions.canEditRepMilestones;
    if (isAdminMilestone) return permissions.canEditAdminMilestones;
    return false;
  };

  const openEditDialog = (milestone) => {
    if (!canEditMilestone(milestone)) return;
    
    const m = milestones[milestone];
    setEditDate(m?.date?.split('T')[0] || new Date().toISOString().split('T')[0]);
    setEditAmount(milestone === 'revenue_received' && m?.amount_lacs ? String(m.amount_lacs) : '');
    setEditDialog({ open: true, milestone });
  };

  const handleSaveMilestone = async () => {
    if (!editDialog.milestone) return;
    
    setSaving(true);
    try {
      const data = {
        milestone: editDialog.milestone,
        date: editDate
      };
      if (editDialog.milestone === 'revenue_received' && editAmount) {
        data.revenue_amount_lacs = parseFloat(editAmount);
      }
      
      await api.updateBookingMilestone(bookingId, data);
      toast.success('Milestone updated successfully');
      setEditDialog({ open: false, milestone: null });
      fetchBookingDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update milestone');
    } finally {
      setSaving(false);
    }
  };

  const handleClearMilestone = async (milestone) => {
    if (!window.confirm('Are you sure you want to clear this milestone?')) return;
    
    try {
      await api.clearBookingMilestone(bookingId, milestone);
      toast.success('Milestone cleared');
      fetchBookingDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear milestone');
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelReason) {
      toast.error('Please select a cancellation reason');
      return;
    }
    
    setCancelling(true);
    try {
      // Use the post-approval cancellation endpoint for Strategy Admin
      await api.cancelBookingPostApproval(bookingId, {
        cancel_reason: cancelReason,
        notes: cancelNotes || undefined
      });
      toast.success('Booking cancelled successfully');
      setShowCancelDialog(false);
      setCancelReason('');
      setCancelNotes('');
      fetchBookingDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const handleReinstateBooking = async () => {
    if (!window.confirm('Are you sure you want to reinstate this cancelled booking?')) return;
    
    try {
      await api.reinstateBooking(bookingId);
      toast.success('Booking reinstated successfully');
      fetchBookingDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reinstate booking');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin mx-auto text-slate-400 mb-3" />
          <p className="text-slate-500">Loading booking...</p>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const tokenPercent = booking.token_percent || 0;
  const tokenLow = tokenPercent < 1;

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-24">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav 
        items={[
          { label: 'My Bookings', path: '/my-bookings' },
          { label: booking.project_name },
          { label: `Unit ${booking.unit_no || '—'}` }
        ]}
        fallbackPath="/my-bookings"
      />
      
      {/* Header */}
      <div className="bg-gradient-to-br from-[#111827] to-[#1F2937] text-white px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1">
            <h1 className="font-bold text-lg truncate">{booking.project_name}</h1>
            <p className="text-xs text-slate-300">Unit: {booking.unit_no || '—'}</p>
          </div>
          <MobileMenuTrigger user={user} className="text-white hover:bg-white/10" />
          <Button
            variant="outline"
            size="sm"
            onClick={fetchBookingDetail}
            className="h-8 bg-transparent border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw size={14} />
          </Button>
        </div>
        
        {/* Financial Summary */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-300">AV</p>
            <p className="font-bold text-lg">{formatLacs(booking.agreement_value_lacs)}</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-300">Token</p>
            <p className="font-bold text-lg">{formatLacs(booking.token_amount_lacs)}</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${tokenLow ? 'bg-[#5B3DF5]/20' : 'bg-emerald-500/20'}`}>
            <p className="text-xs text-slate-300">Token %</p>
            <p className={`font-bold text-lg ${tokenLow ? 'text-red-300' : 'text-emerald-300'}`}>
              {tokenPercent.toFixed(2)}%
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        
        {/* Milestone Progress Section */}
        <Card className="p-4 bg-white rounded-2xl shadow-sm">
          <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <CheckCircle size={18} className="text-emerald-500" />
            Milestone Progress
          </h2>
          
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between relative">
              {/* Progress line */}
              <div className="absolute top-4 left-0 right-0 h-1 bg-slate-200 rounded-full">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${(currentStage / (MILESTONE_ORDER.length - 1)) * 100}%` }}
                />
              </div>
              
              {/* Milestone nodes */}
              {MILESTONE_ORDER.map((key, idx) => {
                const config = MILESTONE_CONFIG[key];
                const milestone = milestones[key];
                const isCompleted = milestone?.completed;
                const isCurrent = idx === currentStage;
                const canEdit = canEditMilestone(key);
                
                return (
                  <div 
                    key={key}
                    className={`relative z-10 flex flex-col items-center cursor-pointer`}
                    onClick={() => canEdit && openEditDialog(key)}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all
                      ${isCompleted ? 'bg-emerald-500 text-white' : 
                        isCurrent ? 'bg-slate-200 border-2 border-emerald-500' : 'bg-slate-200 text-slate-400'}`}
                    >
                      {isCompleted ? <CheckCircle size={16} /> : <Circle size={16} />}
                    </div>
                    <p className={`text-[10px] mt-1 text-center max-w-[50px] ${isCompleted ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
                      {config.shortLabel}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Milestone Details */}
          <div className="space-y-3">
            {MILESTONE_ORDER.map((key) => {
              const config = MILESTONE_CONFIG[key];
              const milestone = milestones[key];
              const isCompleted = milestone?.completed;
              const canEdit = canEditMilestone(key);
              const isAdminOnly = ['invoice_raised', 'revenue_received'].includes(key);
              const Icon = config.icon;
              
              return (
                <div 
                  key={key}
                  className={`p-3 rounded-xl border transition-colors
                    ${isCompleted ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}
                    ${canEdit ? 'cursor-pointer hover:border-slate-300' : ''}`}
                  onClick={() => canEdit && !isCompleted && openEditDialog(key)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isCompleted ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                        <Icon size={16} className={isCompleted ? 'text-emerald-600' : 'text-slate-400'} />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${isCompleted ? 'text-emerald-700' : 'text-slate-600'}`}>
                          {config.label}
                          {isAdminOnly && !permissions.isAdmin && (
                            <Lock size={12} className="inline ml-1 text-slate-400" />
                          )}
                        </p>
                        {isCompleted ? (
                          <p className="text-xs text-slate-500">
                            {formatDate(milestone.date)}
                            {milestone.updated_by && ` • by ${milestone.updated_by}`}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400">
                            {canEdit ? 'Click to update' : isAdminOnly ? 'Admin only' : 'Complete previous step'}
                          </p>
                        )}
                        {key === 'revenue_received' && milestone?.amount_lacs && (
                          <p className="text-xs font-semibold text-emerald-600 mt-1">
                            Revenue: {formatLacs(milestone.amount_lacs)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {isCompleted && canEdit && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditDialog(key); }}
                          className="p-1.5 bg-slate-100 rounded-lg hover:bg-slate-200"
                        >
                          <Edit2 size={14} className="text-slate-500" />
                        </button>
                      )}
                      {isCompleted ? (
                        <CheckCircle size={20} className="text-emerald-500" />
                      ) : canEdit ? (
                        <ChevronRight size={20} className="text-slate-400" />
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Booking Info */}
        <Card className="p-4 bg-white rounded-2xl shadow-sm">
          <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
            <Building2 size={18} className="text-slate-500" />
            Booking Details
          </h2>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Booking ID</span>
              <span className="font-medium text-slate-800">{booking.booking_id}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Booking Date</span>
              <span className="text-slate-800">{formatDate(booking.booking_date || booking.created_at)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100">
              <span className="text-slate-500">Status</span>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                  ${booking.status === 'Cancelled' || booking.status?.includes('CANCELLED') ? 'bg-red-100 text-red-700' :
                    booking.status === 'CRM_VERIFIED' || booking.status === 'STRATEGY_VERIFIED' || booking.verification_status === 'APPROVED' || booking.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' :
                    booking.status === 'QUERY_RAISED' ? 'bg-red-100 text-red-700' :
                    booking.status === 'CITY_HEAD_VERIFIED' ? 'bg-cyan-100 text-cyan-700' :
                    'bg-blue-100 text-blue-700'}`}>
                  {booking.status === 'Cancelled' || booking.status?.includes('CANCELLED') ? 'Cancelled' : 
                   booking.status === 'CRM_VERIFIED' ? 'CRM Approved' : 
                   (booking.status || 'Submitted')}
                </span>
                {/* Low Token Alert flag - separate from status */}
                {(booking.financial_flag === 'LOW_TOKEN_ALERT' || booking.verification_status === 'TOKEN_BLOCK') && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    Low Token
                  </span>
                )}
              </div>
            </div>
            {booking.status === 'Cancelled' && (
              <>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Cancel Reason</span>
                  <span className="text-red-600 text-sm">{booking.cancellation_reason || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Cancelled On</span>
                  <span className="text-slate-600 text-sm">{formatDate(booking.cancelled_at)}</span>
                </div>
              </>
            )}
            {booking.rep_name && (
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Rep</span>
                <span className="text-slate-800">{booking.rep_name}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Customer Info */}
        <Card className="p-4 bg-white rounded-2xl shadow-sm">
          <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
            <User size={18} className="text-slate-500" />
            Customer
          </h2>
          
          <div className="space-y-2 text-sm">
            {booking.customer_name && (
              <div className="flex items-center gap-2">
                <User size={14} className="text-slate-400" />
                <span className="text-slate-800">{booking.customer_name}</span>
              </div>
            )}
            {booking.customer_email && (
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-slate-400" />
                <span className="text-slate-800">{booking.customer_email}</span>
              </div>
            )}
          </div>
        </Card>

        {/* Query Alert & Resubmit Action - Show prominently when query raised */}
        {canResubmit() && (
          <Card className="p-4 bg-red-50 rounded-2xl shadow-sm border-2 border-red-200" data-testid="query-action-card">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-800">Action Required</h3>
                <p className="text-sm text-red-700 mt-1">
                  A query has been raised on this booking. Please review and resubmit with corrections.
                </p>
                {booking.query_remark && (
                  <div className="mt-2 p-2 bg-white rounded-lg border border-red-200">
                    <p className="text-xs text-slate-500">Latest Query:</p>
                    <p className="text-sm text-red-800 font-medium mt-1">{booking.query_remark}</p>
                    {booking.query_raised_by_name && (
                      <p className="text-xs text-slate-500 mt-1">
                        By {booking.query_raised_by_name} ({booking.query_raised_by_role}) • {formatDate(booking.query_raised_at)}
                      </p>
                    )}
                  </div>
                )}
                <Button
                  onClick={openResubmitDialog}
                  className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="resubmit-booking-btn"
                >
                  <Send size={16} className="mr-2" />
                  Edit & Resubmit Booking
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Query History Section */}
        {booking.booking_queries && booking.booking_queries.length > 0 && (
          <Card className="p-4 bg-white rounded-2xl shadow-sm" data-testid="query-history-card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <History size={18} className="text-slate-500" />
                Query History
              </h2>
              <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                {booking.booking_queries.length} {booking.booking_queries.length === 1 ? 'query' : 'queries'}
              </span>
            </div>
            
            <div className="space-y-3">
              {booking.booking_queries.slice().reverse().map((query, idx) => (
                <div 
                  key={query.query_id || idx} 
                  className={`p-3 rounded-xl border ${query.resolved_at ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${query.resolved_at ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {query.resolved_at ? 'Resolved' : 'Open'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {formatDate(query.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-slate-800">{query.remark}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Raised by {query.raised_by_name} ({query.raised_by_role})
                      </p>
                      {query.resolved_at && (
                        <div className="mt-2 pt-2 border-t border-green-200">
                          <p className="text-xs text-green-700">
                            <CheckCircle size={12} className="inline mr-1" />
                            Resolved on {formatDate(query.resolved_at)}
                            {query.resolved_by_name && ` by ${query.resolved_by_name}`}
                          </p>
                          {query.resolution_note && (
                            <p className="text-xs text-slate-600 mt-1 italic">"{query.resolution_note}"</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {booking.resubmission_count > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  <RefreshCw size={12} className="inline mr-1" />
                  This booking has been resubmitted {booking.resubmission_count} time{booking.resubmission_count > 1 ? 's' : ''}
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Documents Section - Version Controlled */}
        <Card className="p-4 bg-white rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <FileText size={18} className="text-slate-500" />
              Booking Documents
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUploadDialog(true)}
              className="text-xs"
            >
              + Upload
            </Button>
          </div>
          
          {/* Legacy Documents (from initial booking) */}
          {(booking.booking_form_url || booking.payment_proof_url) && (
            <div className="flex flex-wrap gap-2 mb-3">
              {booking.booking_form_url && (
                <a
                  href={booking.booking_form_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-[140px] p-2 bg-blue-50 rounded-xl flex items-center gap-2 hover:bg-blue-100"
                >
                  <FileText size={16} className="text-blue-600" />
                  <span className="text-xs text-blue-700 font-medium">Booking Form</span>
                </a>
              )}
              {booking.payment_proof_url && (
                <a
                  href={booking.payment_proof_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-w-[140px] p-2 bg-emerald-50 rounded-xl flex items-center gap-2 hover:bg-emerald-100"
                >
                  <Image size={16} className="text-emerald-600" />
                  <span className="text-xs text-emerald-700 font-medium">Payment Proof</span>
                </a>
              )}
            </div>
          )}
          
          {/* Versioned Documents */}
          {documents.length > 0 ? (
            <div className="space-y-2">
              {documents.map((docGroup) => (
                <div key={docGroup.doc_type} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet size={16} className="text-purple-600" />
                      <span className="font-medium text-sm text-slate-800">{docGroup.label}</span>
                      {docGroup.latest && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          v{docGroup.latest.version}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {docGroup.latest && (
                        <>
                          <a
                            href={docGroup.latest.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View
                          </a>
                          <button
                            onClick={() => handleViewVersionHistory(docGroup.doc_type)}
                            className="text-xs text-slate-500 hover:text-slate-700"
                          >
                            History
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {docGroup.latest && (
                    <div className="mt-1 text-xs text-slate-500">
                      Uploaded by {docGroup.latest.uploaded_by_name} on {formatDate(docGroup.latest.created_at)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-slate-500">
              No versioned documents uploaded yet. Click "Upload" to add documents.
            </div>
          )}
        </Card>
      </div>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Booking Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium">Document Type *</Label>
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cost_sheet">Cost Sheet</SelectItem>
                  <SelectItem value="application_form">Application Form</SelectItem>
                  <SelectItem value="payment_receipt">Payment Receipt</SelectItem>
                  <SelectItem value="agreement_copy">Agreement Copy</SelectItem>
                  <SelectItem value="builder_acknowledgement">Builder Acknowledgement</SelectItem>
                  <SelectItem value="other">Other Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs font-medium">Select File *</Label>
              <div className="mt-1">
                {uploadFile ? (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <CheckCircle size={18} className="text-emerald-600" />
                    <span className="text-sm text-emerald-700 flex-1 truncate">{uploadFile.name}</span>
                    <button
                      onClick={() => setUploadFile(null)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                    <FileSpreadsheet size={24} className="text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Click or drag file here</span>
                    <span className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, Excel (max 10MB)</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
            
            <div>
              <Label className="text-xs font-medium">Remarks (Optional)</Label>
              <Textarea
                value={uploadRemarks}
                onChange={(e) => setUploadRemarks(e.target.value)}
                placeholder="Add any notes about this document..."
                rows={2}
                className="mt-1 rounded-xl"
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)} className="flex-1 rounded-xl">
                Cancel
              </Button>
              <Button
                onClick={handleUploadDocument}
                disabled={!uploadFile || uploading}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
              >
                {uploading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showVersionHistory.open} onOpenChange={(open) => !open && setShowVersionHistory({ open: false, docType: null, versions: [] })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{showVersionHistory.label} - Version History</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-[400px] overflow-y-auto">
            {showVersionHistory.versions.map((doc, idx) => (
              <div key={doc.document_id || idx} className={`p-3 rounded-xl border ${doc.is_latest ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-100'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${doc.is_latest ? 'text-purple-700' : 'text-slate-700'}`}>
                      Version {doc.version}
                    </span>
                    {doc.is_latest && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Latest</span>
                    )}
                  </div>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View
                  </a>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {doc.file_name && <span className="block truncate">{doc.file_name}</span>}
                  Uploaded by {doc.uploaded_by_name} on {formatDate(doc.created_at)}
                </div>
                {doc.remarks && (
                  <div className="mt-1 text-xs text-slate-600 italic">"{doc.remarks}"</div>
                )}
              </div>
            ))}
            {showVersionHistory.versions.length === 0 && (
              <div className="text-center py-4 text-sm text-slate-500">No version history found.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Milestone Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, milestone: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editDialog.milestone && MILESTONE_CONFIG[editDialog.milestone]?.label}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-600">Date</Label>
              <Input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="mt-1"
              />
            </div>
            
            {editDialog.milestone === 'revenue_received' && (
              <div>
                <Label className="text-sm text-slate-600">Revenue Amount (Lacs)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    placeholder="0.00"
                    className="pl-7 pr-12"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Lacs</span>
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              {milestones[editDialog.milestone]?.completed && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditDialog({ open: false, milestone: null });
                    handleClearMilestone(editDialog.milestone);
                  }}
                  className="text-[#5B3DF5] border-red-200 hover:bg-red-50"
                >
                  Clear
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setEditDialog({ open: false, milestone: null })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMilestone}
                disabled={saving}
                className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog - REMOVED: Cancellation only available from All Bookings panel */}
      {/* Cancel functionality moved to Sales Intelligence > All Bookings for CRM Support role */}

      {/* Resubmit Booking Dialog (for CA/Rep) */}
      <Dialog open={showResubmitDialog} onOpenChange={setShowResubmitDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Send size={20} />
              Edit & Resubmit Booking
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            {booking?.query_remark && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-slate-500 mb-1">Query to address:</p>
                <p className="text-sm text-amber-800 font-medium">{booking.query_remark}</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-slate-600">Agreement Value (Lacs)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={resubmitData.agreement_value_lacs}
                  onChange={(e) => setResubmitData({...resubmitData, agreement_value_lacs: e.target.value})}
                  placeholder="Enter value"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Unit Number</Label>
                <Input
                  value={resubmitData.unit_number}
                  onChange={(e) => setResubmitData({...resubmitData, unit_number: e.target.value})}
                  placeholder="e.g. 1201"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm text-slate-600">Tower</Label>
              <Input
                value={resubmitData.tower}
                onChange={(e) => setResubmitData({...resubmitData, tower: e.target.value})}
                placeholder="e.g. A, B, Tower-1"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-sm text-slate-600">Customer Name</Label>
              <Input
                value={resubmitData.customer_name}
                onChange={(e) => setResubmitData({...resubmitData, customer_name: e.target.value})}
                placeholder="Full name"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-slate-600">Phone</Label>
                <Input
                  value={resubmitData.customer_phone}
                  onChange={(e) => setResubmitData({...resubmitData, customer_phone: e.target.value})}
                  placeholder="Phone number"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Email</Label>
                <Input
                  type="email"
                  value={resubmitData.customer_email}
                  onChange={(e) => setResubmitData({...resubmitData, customer_email: e.target.value})}
                  placeholder="Email address"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-sm text-slate-600">Correction Notes *</Label>
              <Textarea
                value={resubmitData.correction_notes}
                onChange={(e) => setResubmitData({...resubmitData, correction_notes: e.target.value})}
                placeholder="Explain what was corrected/fixed..."
                className="mt-1"
                rows={3}
              />
              <p className="text-xs text-slate-400 mt-1">Required: Describe what changes were made to address the query</p>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowResubmitDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResubmitBooking}
                disabled={resubmitting || !resubmitData.correction_notes?.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-resubmit-btn"
              >
                {resubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Resubmitting...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Resubmit Booking
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Actions - REMOVED: Cancel functionality moved to All Bookings panel for CRM Support */}
      {/* Booking detail page shows only info, milestones, and verification history */}
    </div>
  );
};

export default BookingDetail;
